import { Add, DeleteOutline, Settings } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { WidgetDto } from '@thingslog/repositories';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardConfirmationDialog } from '../../confirmation-dialog/DashboardConfirmationDialog';
import { DashboardSidebarWidgetCard } from './DashboardSidebarWidgetCard';

interface Props {
  widgetId: string;
  dashboardWidgets: WidgetDto[];
  closable?: boolean;
  isDeletingWidget: boolean;
  onAddWidget: () => void;
  onEditWidget: (dashboardWidget: WidgetDto) => void;
  onDeleteWidget: (dashboardWidget: WidgetDto, callbacks?: { onSuccess?: () => void }) => void;
  onClose: (widgetId: string) => void;
  onStartTour: () => void;
}

export const DashboardSidebarDashboardWidgetsWidget: FC<Props> = ({
  widgetId,
  dashboardWidgets,
  closable = true,
  isDeletingWidget,
  onAddWidget,
  onEditWidget,
  onDeleteWidget,
  onClose,
  onStartTour,
}: Props) => {
  const { t } = useTranslation();
  const [widgetPendingDelete, setWidgetPendingDelete] = useState<WidgetDto | null>(null);

  const handleDeleteConfirm = (): void => {
    if (!widgetPendingDelete) {
      return;
    }

    onDeleteWidget(widgetPendingDelete, {
      onSuccess: () => setWidgetPendingDelete(null),
    });
  };

  return (
    <DashboardSidebarWidgetCard
      title={t('dashboard_widgets', 'Widgets')}
      widgetId={widgetId}
      expandable
      closable={closable}
      onClose={onClose}
      onStartTour={onStartTour}
    >
      <div className="flex min-w-0 flex-col gap-3 p-4">
        <Button
          variant="contained"
          disableElevation
          fullWidth
          startIcon={<Add />}
          onClick={onAddWidget}
        >
          {t('dashboard_add_widget', 'Add Widget')}
        </Button>

        {dashboardWidgets.length === 0 && (
          <div className="rounded-md border border-solid border-gray-200 p-3 text-center">
            <Typography variant="body2" className="text-gray-600">
              {t('dashboard_no_widgets_yet', 'This dashboard does not have any widgets yet.')}
            </Typography>
          </div>
        )}

        {dashboardWidgets.map((dashboardWidget: WidgetDto) => (
          <div
            key={dashboardWidget.id}
            className="flex min-w-0 flex-col gap-2 rounded-md border border-solid border-gray-200 p-3"
          >
            <div className="min-w-0">
              <Typography variant="subtitle2" className="truncate">
                {dashboardWidget.settings.title ||
                  t('dashboard_untitled_widget', 'Untitled widget')}
              </Typography>
              <Typography variant="caption" className="text-gray-600">
                {dashboardWidget.settings.type}
              </Typography>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Settings />}
                onClick={(): void => onEditWidget(dashboardWidget)}
              >
                {t('edit', 'Edit')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteOutline />}
                disabled={isDeletingWidget}
                onClick={(): void => setWidgetPendingDelete(dashboardWidget)}
              >
                {t('delete', 'Delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <DashboardConfirmationDialog
        open={!!widgetPendingDelete}
        title={t('dashboard_delete_widget', 'Delete Widget')}
        confirmLabel={t('dashboard_yes_delete', 'Yes, delete')}
        disabled={isDeletingWidget}
        onClose={(): void => setWidgetPendingDelete(null)}
        onConfirm={handleDeleteConfirm}
      >
        {t(
          'dashboard_widget_delete_confirmation_prefix',
          'This will permanently delete the widget'
        )}{' '}
        <strong>{widgetPendingDelete?.settings.title || t('dashboard_widget', 'Widget')}</strong>{' '}
        {t(
          'dashboard_widget_delete_confirmation_suffix',
          'from the remote dashboard configuration. This action cannot be undone.'
        )}
      </DashboardConfirmationDialog>
    </DashboardSidebarWidgetCard>
  );
};
